import React from 'react';
import Dropdown from '@src/elements/Dropdown';
import styles from './LocationFilters.module.scss';
import { useRouter } from 'next/router';
import { PATHS } from '@src/constants/site';
import { MapLocation, MapLocationOptions } from '@src/types/map';
import { DropdownItem } from '@src/elements/Dropdown/Dropdown';
import { ParsedUrlQuery } from 'querystring';

interface LocationFiltersProps {
  locationOptions?: MapLocationOptions;
  pathName?: string;
  useQueryParams?: boolean;
  showAreaFilter?: boolean;
  showSuburbFilter?: boolean;
}

const LocationFilters: React.FC<LocationFiltersProps> = ({
  locationOptions,
  pathName = PATHS.FIND_YOUR_COMMUNITY,
  useQueryParams = false,
  showAreaFilter = false,
  showSuburbFilter = false
}) => {
  const router = useRouter();

  const selectedLocationArray = router.asPath
    .split('/')
    .filter(Boolean)
    .slice(1);
  const selectedLocationPathName = {
    state: selectedLocationArray?.[0] || 'all',
    region: selectedLocationArray?.[1] || 'all',
    area: selectedLocationArray?.[2] || 'all',
    suburb: selectedLocationArray?.[3] || 'all'
  };
  // The NewsEventsListingGrid component uses the query params to set the selected location
  // rather than the pathname
  const selectedLocationQueryParams = {
    state: (router.query.state as string) || 'all',
    region: (router.query.region as string) || 'all',
    area: (router.query.area as string) || 'all',
    suburb: (router.query.suburb as string) || 'all'
  };
  const selectedLocation = useQueryParams
    ? selectedLocationQueryParams
    : selectedLocationPathName;

  const handleParamsFiltering = (params: Record<string, string>) =>
    Object.entries(params)
      .filter(([, value]) => value !== 'all' && value !== undefined)
      .map(([key, value]) => ({
        [key]: value
      }));

  const getBaseParams = (
    routerParams: ParsedUrlQuery,
    currentFilter: string,
    newParams: MapLocation
  ) => {
    const paramsToRemove = Object.entries(newParams)
      .filter(([, value]) => value === 'all')
      .map(([key]) => key);

    return Object.entries(routerParams)
      .filter(([key]) => key !== 'slug_national_standard')
      .filter(([key]) => key !== currentFilter)
      .filter(([key]) => !paramsToRemove.includes(key))
      .map(([key, value]) => ({
        [key]: value
      }));
  };

  const locationLevels = [
    {
      key: 'state',
      label: 'State',
      getOptions: () => [
        { id: 'all', label: 'State', key: 'all' },
        ...Object.values(locationOptions?.states || {})
      ],
      getSelectedValue: () => selectedLocation?.state
    },
    {
      key: 'region',
      label: 'Region',
      getOptions: () => [
        { id: 'all', label: 'Region', key: 'all' },
        ...Object.values(
          (selectedLocation?.state &&
            locationOptions?.states?.[selectedLocation?.state]?.regions) ||
            {}
        )
      ],
      getSelectedValue: () => selectedLocation?.region
    },
    {
      key: 'area',
      label: 'Area',
      getOptions: () => [
        { id: 'all', label: 'Area', key: 'all' },
        ...Object.values(
          (selectedLocation?.state &&
            selectedLocation?.region &&
            locationOptions?.states?.[selectedLocation?.state]?.regions?.[
              selectedLocation?.region
            ]?.areas) ||
            {}
        )
      ],
      getSelectedValue: () => selectedLocation?.area
    },
    {
      key: 'suburb',
      label: 'Suburb',
      getOptions: () => [
        { id: 'all', label: 'Suburb', key: 'all' },
        ...Object.values(
          (selectedLocation?.state &&
            selectedLocation?.region &&
            selectedLocation?.area &&
            locationOptions?.states?.[selectedLocation?.state]?.regions?.[
              selectedLocation?.region
            ]?.areas?.[selectedLocation?.area]?.suburbs) ||
            {}
        )
      ],
      getSelectedValue: () => selectedLocation?.suburb
    }
  ]
    // ! Currently Levande aren't sure what filters to use where, so we need to have optional filters
    // ! that can be passed in to the component
    // These are likely to be removed in future phases once they make a decision
    .filter((item) => (showAreaFilter ? item : item.key !== 'area'))
    .filter((item) => (showSuburbFilter ? item : item.key !== 'suburb'));

  return (
    <div className={styles.filterContainer}>
      {locationLevels.map((level) => (
        <Dropdown
          key={level.key}
          plain
          ariaLabel={level.label}
          options={level.getOptions() as DropdownItem[]}
          placeholder={
            level.getSelectedValue() === 'all' ||
            level.getSelectedValue() === undefined
          }
          onItemSelected={(id) => {
            // Reset to 'all' if higher levels in hierarchy are changed, otherwise use existing value
            const newState =
              level.key === 'state' ? id : selectedLocation?.state;
            const newRegion =
              level.key === 'region'
                ? id
                : level.key === 'state'
                  ? 'all'
                  : selectedLocation?.region;
            const newArea =
              level.key === 'area'
                ? id
                : level.key === 'region' || level.key === 'state'
                  ? 'all'
                  : selectedLocation?.area;
            const newSuburb =
              level.key === 'suburb'
                ? id
                : level.key === 'area' ||
                    level.key === 'region' ||
                    level.key === 'state'
                  ? 'all'
                  : selectedLocation?.suburb;

            const newPath = [pathName, newState, newRegion, newArea, newSuburb]
              .filter((item) => item !== 'all' && item !== undefined)
              .join('/');

            // Only used if using query params instead of path routing
            const baseParams = getBaseParams(router.query, level.key, {
              state: newState,
              region: newRegion,
              area: newArea,
              suburb: newSuburb
            });
            const newQueryParams = handleParamsFiltering({
              ...(newState && { state: newState }),
              ...(newRegion && { region: newRegion }),
              ...(newArea && { area: newArea }),
              ...(newSuburb && { suburb: newSuburb })
            });

            router.push(
              {
                pathname: `/${useQueryParams ? pathName : newPath}/`,
                query: {
                  ...(useQueryParams && Object.assign({}, ...baseParams)),
                  ...(useQueryParams && Object.assign({}, ...newQueryParams)),
                  page: 1
                }
              },
              undefined,
              { shallow: useQueryParams }
            );
          }}
          onClose={() => {}}
          value={level.getSelectedValue()}
        />
      ))}
    </div>
  );
};

export default LocationFilters;
